// Tipos, diccionario de estatus y utilidades puras del "Cruce en listas restringidas" (PLD).
// Fuente única de verdad para estatus, textos y colores de la funcionalidad.

export type BlacklistStatusType =
    | "not_executed"
    | "incomplete_error"
    | "manual_review"
    | "approved"
    | "match_confirmed"
    | "manual_rejected";

export type ManualCheckStatus = "approved" | "rejected";

export interface ManualCheckData {
    status?: ManualCheckStatus;
    detail?: string;
    resolved_by?: string;
    resolved_at?: string;
}

export interface ManualCheckPayload {
    uuid: string;
    manualcheck: {
        status: ManualCheckStatus;
        detail: string;
        resolved_by: string;
        resolved_at: string;
    };
}

export interface PldValidation {
    uuid?: string;
    type?: string;
    status?: string;
    result?: string;
    manualcheck?: ManualCheckData;
    manualCheck?: ManualCheckData;
}

export interface StatusStyleConfig {
    label: string;
    badgeBg: string;
    badgeColor: string;
    border: string;
    statusLabel: string;
    alertConfig?: {
        severity: "error" | "warning" | "success";
        title: string;
        message: string;
        bgColor: string;
        borderColor: string;
        textColor: string;
    };
}

export interface MatchInfo {
    text: string;
    color: string;
}

export const PLD_COLORS = {
    green: "#27AE60",
    red: "#C0392B",
    redStrong: "#C92A2A",
    orange: "#D35400",
    blue: "#2B368B",
    neutral: "#495057",
};

const noAnswerSuffix =
    "El cruce no se marca como aprobado: reintenta el cruce manual y, si persiste, escala con el equipo de sistemas.";

// Diccionario de configuración para centralizar estilos, textos y alertas según el estado PLD
export const PLD_STATUS_DICTIONARY: Record<BlacklistStatusType, StatusStyleConfig> = {
    not_executed: {
        label: "Cruce pendiente",
        badgeBg: "#E9ECEF",
        badgeColor: "#495057",
        border: "1px solid #CED4DA",
        statusLabel: "Pendiente — sin ejecutar",
        alertConfig: {
            severity: "warning",
            title: "Cruce pendiente",
            message:
                "Aún no se han ejecutado las validaciones de listas restringidas y blacklist interna para este expediente. La cuenta no puede avanzar hasta completar el cruce.",
            bgColor: "#FFF9DB",
            borderColor: "#FFF3BF",
            textColor: "#D9480F",
        },
    },
    incomplete_error: {
        label: "Cruce incompleto",
        badgeBg: "#FFE3E3",
        badgeColor: "#C92A2A",
        border: "2px solid #E74C3C",
        statusLabel: "Incompleto — error de sistema",
        alertConfig: {
            severity: "error",
            title: "Error de sistema en el cruce",
            message: `Los servicios de listas restringidas y blacklist interna no respondieron. ${noAnswerSuffix}`,
            bgColor: "#FFF5F5",
            borderColor: "#FFE3E3",
            textColor: "#C92A2A",
        },
    },
    manual_review: {
        label: "En revisión de Manual Check",
        badgeBg: "#FFF3BF",
        badgeColor: "#D9480F",
        border: "2px solid #E67E22",
        statusLabel: "Completo — en revisión de Manual Check",
        alertConfig: {
            severity: "warning",
            title: "Coincidencia inexacta en revisión",
            message:
                "Se registra coincidencia para revisión manual. Se deberá analizar si se trata de la misma persona y en su caso indicar si se continúa con la relación comercial con el Prospecto de Cliente. (Administrador: solo nombre, sin fecha de nacimiento). Abre Sí para consultar los cruces realizados y registrar el análisis manual; hasta entonces la coincidencia no se aprueba.",
            bgColor: "#FFF9DB",
            borderColor: "#FFF3BF",
            textColor: "#D9480F",
        },
    },
    approved: {
        label: "Cruce aprobado",
        badgeBg: "#E6FCF5",
        badgeColor: "#0CA678",
        border: "1px solid #2ECC71",
        statusLabel: "Completo — sin coincidencias",
        alertConfig: {
            severity: "success",
            title: "Cruce aprobado",
            message: "Sin coincidencias en listas restringidas ni blacklist interna. El expediente puede avanzar a Contrato enviado.",
            bgColor: "#E6FCF5",
            borderColor: "#C3FAFA",
            textColor: "#0CA678",
        },
    },
    match_confirmed: {
        label: "Coincidencia exacta",
        badgeBg: "#FFE3E3",
        badgeColor: "#C92A2A",
        border: "2px solid #E74C3C",
        statusLabel: "Completo — coincidencia exacta",
        alertConfig: {
            severity: "error",
            title: "Coincidencia exacta",
            message: 'La cuenta no debe aperturarse: se genera alerta y evidencia y "Contrato enviado" permanece bloqueado.',
            bgColor: "#FFF5F5",
            borderColor: "#FFC9C9",
            textColor: "#C92A2A",
        },
    },
    manual_rejected: {
        label: "Cruce no aprobado",
        badgeBg: "#FFE3E3",
        badgeColor: "#C92A2A",
        border: "2px solid #E74C3C",
        statusLabel: "Completo — cruce no aprobado",
        alertConfig: {
            severity: "error",
            title: "Cruce no aprobado",
            message:
                'El cruce manual quedó NO aprobado. La cuenta no puede abrirse: se genera alerta y evidencia y "Contrato enviado" permanece bloqueado.',
            bgColor: "#FFF5F5",
            borderColor: "#FFC9C9",
            textColor: "#C92A2A",
        },
    },
};

// Textos específicos para un "approved" resuelto por cruce manual (distinto de "sin coincidencias")
export const APPROVED_BY_MANUAL_STATUS_LABEL = "Completo — resuelto en cruce manual de Manual Check";
export const APPROVED_BY_MANUAL_MESSAGE = "El cruce manual quedó aprobado, así que el expediente puede continuar con la apertura de cuenta.";

export const getManualCheck = (item?: PldValidation): ManualCheckData | undefined =>
    item?.manualcheck || item?.manualCheck;

export const isManualCleared = (manualCheck?: ManualCheckData): boolean => {
    const status = manualCheck?.status as string | undefined;
    return status === "cleared" || status === "approved";
};

export const isManualRejected = (manualCheck?: ManualCheckData): boolean =>
    manualCheck?.status === "rejected";

export interface MatchDetails {
    uuid: string;
    entity?: string;
    fullName?: string;
    identifier?: string;
    manualcheck?: ManualCheckData;
}

export const typeBlackList: Record<string, string> = {
    "social-reason": "Razón social",
    "shareholder": "Accionista",
    "real-owner": "Propietario real",
    "admin": "Administrador",
    "legal-representative": "Representante legal",
};

export const getBlacklistFullName = (item?: any): string => {
    if (item?.entity) {
        const { name = "", lname = "", slname = "" } = item.entity;
        const full = `${name} ${lname} ${slname}`.trim();
        if (full) return full;
    }
    return item?.name || "N/D";
};

export const getBlacklistTaxId = (item?: any): string =>
    item?.entity?.tax_id || item?.entity?.population_id || item?.tax_id || item?.rfc || "";

export const buildManualCheckDetails = (item?: any): MatchDetails => ({
    uuid: item?.uuid,
    entity: typeBlackList[item?.type] || item?.type,
    fullName: getBlacklistFullName(item),
    identifier: getBlacklistTaxId(item),
    manualcheck: getManualCheck(item),
});

export interface ManualCheckSummary {
    state: "none" | "cleared" | "rejected";
    manualCheck?: ManualCheckData;
    hasCleared: boolean;
    hasRejected: boolean;
}

// Un solo rechazo prevalece sobre cualquier aprobado
export const getManualCheckSummary = (validations: PldValidation[] = []): ManualCheckSummary => {
    const manualChecks = validations.map(getManualCheck).filter(Boolean) as ManualCheckData[];
    const hasRejected = manualChecks.some(isManualRejected);
    const hasCleared = manualChecks.some(isManualCleared);
    const manualCheck = hasRejected
        ? manualChecks.find(isManualRejected)
        : hasCleared
            ? manualChecks.find(isManualCleared)
            : undefined;

    return {
        state: hasRejected ? "rejected" : hasCleared ? "cleared" : "none",
        manualCheck,
        hasCleared,
        hasRejected,
    };
};

export const formatManualTimestamp = (value?: string): string => {
    if (!value) return "";
    const date = new Date(value);
    return isNaN(date.getTime()) ? value : date.toLocaleString("es-MX");
};

export const formatManualCheckMeta = (manualCheck?: ManualCheckData): string => {
    if (!manualCheck) return "";
    const by = manualCheck.resolved_by ? ` — registrado por ${manualCheck.resolved_by}` : "";
    const at = manualCheck.resolved_at ? `, ${formatManualTimestamp(manualCheck.resolved_at)}` : "";
    return `${by}${at}`;
};

// Deriva el estatus del cruce a partir de las validaciones (fuente única de verdad)
export const resolvePldStatus = (
    externalValidations: PldValidation[] = [],
    internalValidations: PldValidation[] = [],
    fallback?: BlacklistStatusType
): BlacklistStatusType | undefined => {
    const all = [...(externalValidations || []), ...(internalValidations || [])];
    if (all.length === 0) return fallback;

    if (all.some((i) => i?.status === "error")) return "incomplete_error";
    // Coincidencia en blacklist interna => coincidencia exacta (no permite cruce manual)
    if ((internalValidations || []).some((i) => i?.result === "matched")) return "match_confirmed";
    // Coincidencia en listas restringidas (external) pendiente de cruce manual
    if ((externalValidations || []).some((i) => i?.result === "matched" && !getManualCheck(i))) return "manual_review";
    // Cruce manual rechazado => cruce no aprobado
    if ((externalValidations || []).some((i) => isManualRejected(getManualCheck(i)))) return "manual_rejected";

    return "approved";
};

// Nombres internos conocidos del estatus "Contrato enviado" (fallback).
export const CONTRACT_SENT_STATUS_NAMES = ["sent", "contract_sent"];

// Identifica de forma estable el estatus "Contrato enviado".
// Prioriza una bandera explícita del catálogo (metadata.is_contract_sent);
// si no existe, cae al nombre interno y, como último recurso, a la descripción.
export const isContractSentStatus = (status?: any): boolean => {
    if (!status) return false;
    if (status?.metadata?.is_contract_sent === true) return true;
    if (CONTRACT_SENT_STATUS_NAMES.includes(status?.name)) return true;
    const description = status?.metadata?.description;
    return typeof description === "string" && description.toLowerCase().includes("contrato enviado");
};

export const hasError = (list: PldValidation[] = []): boolean => (list || []).some((i) => i?.status === "error");
export const getMatchInfo = (list: PldValidation[] = []): MatchInfo => {
    if (hasError(list)) return { text: "No se realizó el cruce", color: PLD_COLORS.redStrong };
    return (list || []).some((i) => i?.result === "matched")
        ? { text: "Con Coincidencia", color: PLD_COLORS.redStrong }
        : { text: "Sin Coincidencia", color: PLD_COLORS.green };
};

// Coincidencia externa: si todas las coincidencias ya fueron descartadas en cruce manual, se muestra en verde
export const getExternalMatchInfo = (externalValidations: PldValidation[] = []): MatchInfo => {
    const matched = (externalValidations || []).filter((i) => i?.result === "matched");
    const allCleared = matched.length > 0 && matched.every((i) => isManualCleared(getManualCheck(i)));
    return allCleared ? { text: "Descartada en cruce manual", color: PLD_COLORS.green } : getMatchInfo(externalValidations);
};

export const getSourcesText = (externalValidations: PldValidation[] = [], internalValidations: PldValidation[] = []): string =>
    `${hasError(externalValidations) ? "Listas restringidas (sin respuesta)" : "Listas restringidas"} · ${
        hasError(internalValidations) ? "Blacklist interna (sin respuesta)" : "Blacklist interna"
    }`;

export const getFailedSources = (externalValidations: PldValidation[] = [], internalValidations: PldValidation[] = []): string[] =>
    [
        hasError(externalValidations) ? "listas restringidas" : null,
        hasError(internalValidations) ? "blacklist interna" : null,
    ].filter(Boolean) as string[];

// Entidades cruzadas: válidas sobre total, con los tipos (sin duplicados) según el diccionario
export const getEntitiesText = (validations: PldValidation[] = [], typeLabels: Record<string, string> = {}): string => {
    const valid = (validations || []).filter((i) => i?.status !== "error").length;
    const types = Array.from(new Set((validations || []).map((i) => i?.type).filter(Boolean)))
        .map((t) => typeLabels[t as string] || t)
        .join(", ");
    return `${valid} de ${validations.length}${types ? ` (${types})` : ""}`;
};

export const getErrorAlertMessage = (failedSources: string[], config?: StatusStyleConfig): string | undefined => {
    if (!config?.alertConfig) return undefined;
    if (failedSources.length === 0) return config.alertConfig.message;
    return `Los servicios de ${failedSources.join(" y ")} no respondieron. ${noAnswerSuffix}`;
};
